/**
 * info-tooltip.ts - canonical `i` circle info button per amitone-design/studio.html:1545
 * and amitone-design/studio.css:478-514.
 *
 * Usage:
 *   const tip = createInfoTooltip('My tooltip description text');
 *   headerElement.appendChild(tip.element);
 *
 * The popover opens on hover / focus-visible and closes on mouse-leave + blur.
 * API is identical to the previous implementation so call sites require no changes.
 *
 * R8 B4: The .tip span is now a portal -- it is appended to document.body on
 * hover/focus and removed on leave/blur.  This escapes all ancestor stacking
 * contexts and overflow:hidden parents that previously clipped the tip.
 */

export interface InfoTooltipHandle {
  /** The icon button element to append to a header or label. */
  element: HTMLButtonElement;
  /** Update the tooltip text (e.g. on locale change). */
  setText(text: string): void;
}

/** Shared portal node reused across all tooltips (only one visible at a time). */
let portalNode: HTMLElement | null = null;

/** Currently active tooltip text (updated by setText while portal is shown). */
let activeHandle: { tip: HTMLElement; text: string } | null = null;

/** Handle that owns the currently visible shared portal. */
let activeOwnerHandle: InfoTooltipHandle | null = null;

const VIEWPORT_MARGIN = 8;

/**
 * Return (and lazily create) the shared portal element.
 * It carries the .info-tip-portal class for visual styling in styles.css.
 */
function getPortal(): HTMLElement {
  if (!portalNode) {
    portalNode = document.createElement('span');
    portalNode.className = 'info-tip-portal';
  }
  return portalNode;
}

/**
 * Position the portal below (or above if near viewport bottom) the trigger button.
 */
function positionPortal(portal: HTMLElement, btn: HTMLElement): void {
  const rect = btn.getBoundingClientRect();
  const OFFSET = 4; // px gap between button bottom and tip top
  const tipHeight = portal.getBoundingClientRect().height || 80; // estimate before first paint

  const spaceBelow = window.innerHeight - rect.bottom;
  const flipToAbove = spaceBelow < tipHeight + OFFSET + 8;

  if (flipToAbove) {
    portal.style.top = `${rect.top - tipHeight - OFFSET}px`;
  } else {
    portal.style.top = `${rect.bottom + OFFSET}px`;
  }

  // Align left edge with button; clamp to keep within viewport.
  const leftCandidate = rect.left;
  const tipWidth = portal.getBoundingClientRect().width || 220;
  const maxLeft = window.innerWidth - tipWidth - VIEWPORT_MARGIN;
  let left = Math.min(leftCandidate, maxLeft);
  if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
  portal.style.left = `${left}px`;
}

const viewportChangeHandler = (): void => {
  hidePortal();
};

let viewportListenersAttached = false;

function attachViewportListeners(): void {
  if (viewportListenersAttached) return;
  window.addEventListener('scroll', viewportChangeHandler, {
    passive: true,
    capture: true,
  });
  window.addEventListener('resize', viewportChangeHandler, { passive: true });
  viewportListenersAttached = true;
}

function detachViewportListeners(): void {
  if (!viewportListenersAttached) return;
  window.removeEventListener('scroll', viewportChangeHandler, { capture: true });
  window.removeEventListener('resize', viewportChangeHandler);
  viewportListenersAttached = false;
}

/**
 * Show the portal for a given button and text.
 * Called on mouseenter and focusin.
 */
function showPortal(
  btn: HTMLElement,
  text: string,
  ownerHandle: InfoTooltipHandle,
): void {
  const portal = getPortal();
  portal.textContent = text;
  portal.className = btn.classList.contains('info-btn-wide')
    ? 'info-tip-portal info-tip-portal-wide'
    : 'info-tip-portal';

  // Remove from any previous parent first (safety -- should not happen in practice).
  if (portal.parentNode && portal.parentNode !== document.body) {
    portal.parentNode.removeChild(portal);
  }

  document.body.appendChild(portal);
  // Position after appending so getBoundingClientRect is accurate.
  positionPortal(portal, btn);

  activeHandle = { tip: portal, text };
  activeOwnerHandle = ownerHandle;
  attachViewportListeners();
}

/**
 * Hide and remove the portal from the DOM.
 * Called on mouseleave and focusout.
 */
function hidePortal(): void {
  detachViewportListeners();
  if (portalNode && portalNode.parentNode) {
    portalNode.parentNode.removeChild(portalNode);
  }
  activeHandle = null;
  activeOwnerHandle = null;
}

/**
 * Create a canonical `i` info-tooltip circle button.
 *
 * Matches amitone-design/studio.html:1545 `<button class="info-btn">i<span class="tip">...</span></button>`
 * and amitone-design/studio.css:478-514 `.info-btn` spec:
 *   - 13x13px circle, 1px var(--fg-mute) border
 *   - Text: literal lowercase `i` (NO parentheses) in font-mono 9px bold
 *   - Hover / focus-visible: border + text -> var(--violet)
 *   - .tip child: 220px wide, opens on hover (not click)
 *
 * R8 B4 portal pattern: the tip span is appended to document.body on hover/focus
 * via getBoundingClientRect positioning (position:fixed).  This escapes all
 * ancestor stacking contexts and overflow:hidden parents.
 *
 * @param text - The visible tooltip description.
 * @param ariaLabel - Screen-reader label for the button (defaults to text).
 */
export function createInfoTooltip(text: string, ariaLabel?: string): InfoTooltipHandle {
  let currentText = text;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'info-btn';
  // Canonical text is bare `i` per amitone-design/studio.html:1545. The earlier
  // R5 implementation used `(i)` with parentheses which was a misread of the
  // canonical reference (and Yuko spotted it visually).
  btn.textContent = 'i';
  btn.setAttribute('aria-label', ariaLabel ?? text);

  const handle: InfoTooltipHandle = {
    element: btn,
    setText(newText: string): void {
      currentText = newText;
      btn.setAttribute('aria-label', newText);
      // If the portal is currently shown for this button, update its text live.
      if (
        activeOwnerHandle === handle &&
        activeHandle &&
        activeHandle.tip === portalNode &&
        portalNode?.parentNode
      ) {
        portalNode.textContent = newText;
        activeHandle.text = newText;
      }
    },
  };

  // ---- Mouse events ----

  btn.addEventListener('mouseenter', () => {
    showPortal(btn, currentText, handle);
  });

  btn.addEventListener('mouseleave', () => {
    hidePortal();
  });

  // ---- Keyboard / focus events ----

  btn.addEventListener('focusin', () => {
    showPortal(btn, currentText, handle);
  });

  btn.addEventListener('focusout', () => {
    hidePortal();
  });

  return handle;
}
